import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Theme options
export const THEMES = {
    LIGHT: 'light',
    DARK: 'dark',
    SYSTEM: 'system'
};

const ThemeContext = createContext(null);

// Get system preference
function getSystemTheme() {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.LIGHT;
    }
    return THEMES.LIGHT;
}

// Get stored theme or default
function getStoredTheme() {
    if (typeof window !== 'undefined') {
        return localStorage.getItem('3ppms-theme') || THEMES.LIGHT;
    }
    return THEMES.LIGHT;
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(THEMES.LIGHT);
    const [resolvedTheme, setResolvedTheme] = useState(THEMES.LIGHT);

    // Initialize theme from localStorage
    useEffect(() => {
        const stored = getStoredTheme();
        setThemeState(stored);

        if (stored === THEMES.SYSTEM) {
            setResolvedTheme(getSystemTheme());
        } else {
            setResolvedTheme(stored);
        }
    }, []);

    // Listen for system theme changes
    useEffect(() => {
        if (theme !== THEMES.SYSTEM) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            setResolvedTheme(e.matches ? THEMES.DARK : THEMES.LIGHT);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // Apply theme to document
    useEffect(() => {
        const root = document.documentElement;

        if (resolvedTheme === THEMES.DARK) {
            root.classList.add('dark');
            root.style.colorScheme = 'dark';
        } else {
            root.classList.remove('dark');
            root.style.colorScheme = 'light';
        }
    }, [resolvedTheme]);

    // Set theme and persist
    const setTheme = useCallback((newTheme) => {
        setThemeState(newTheme);
        localStorage.setItem('3ppms-theme', newTheme);

        if (newTheme === THEMES.SYSTEM) {
            setResolvedTheme(getSystemTheme());
        } else {
            setResolvedTheme(newTheme);
        }
    }, []);

    // Toggle between light and dark
    const toggleTheme = useCallback(() => {
        const newTheme = resolvedTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
        setTheme(newTheme);
    }, [resolvedTheme, setTheme]);

    const value = {
        theme,           // The setting (light/dark/system)
        resolvedTheme,   // The actual applied theme (light/dark)
        setTheme,
        toggleTheme,
        isDark: resolvedTheme === THEMES.DARK
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

export default ThemeContext;
